import React, { useMemo, useState } from "react";
import {
  Box, Card, CardContent, Typography, Button, Grid, RadioGroup, Radio, FormControlLabel, Alert, LinearProgress
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import AdminLayout from "./AdminLayout";
import creditService from "../services/creditService";
import { saveAssessmentResult, getAssessmentLock, lockAssessmentForUser } from "../services/assessmentService";
import { getOnboardingQuizConfig } from "../services/quizService";
import { validateQuizJSON } from "../utils/quizSchema";

const DEFAULT_QUESTIONS = [
  { id: "q1", text: "What is the primary goal of this portal?", options: ["Entertainment", "Startup evaluation and learning", "File storage"], correct: 1, credit: 96 },
  { id: "q2", text: "How many credits are required to unlock the dashboard?", options: ["200", "480", "600"], correct: 1, credit: 96 },
  { id: "q3", text: "Each Learning Path awards how many credits?", options: ["100", "50", "10"], correct: 0, credit: 96 },
  { id: "q4", text: "Who can manually add credits and grant access?", options: ["Any user", "Admin only", "Guests"], correct: 1, credit: 96 },
  { id: "q5", text: "What happens after you unlock the dashboard?", options: ["480 credits are deducted", "Credits increase", "Nothing"], correct: 0, credit: 96 }
];

export default function AssessmentTestPage() {
  const { user, userProfile } = useAuth();
  const navigate = useNavigate();
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [score, setScore] = useState(null);
  const [locked, setLocked] = useState(false);
  const [checking, setChecking] = useState(true);
  const [questions, setQuestions] = useState(DEFAULT_QUESTIONS);

  React.useEffect(() => {
    let active = true;
    async function loadQuiz() {
      try {
        const cfg = await getOnboardingQuizConfig();
        if (!active || !cfg) return;
        const valid = validateQuizJSON(cfg);
        if (valid.valid) {
          setQuestions(cfg.questions.map((q, idx) => ({
            id: q.id || `q${idx+1}`,
            text: q.text,
            options: q.options,
            correct: q.correct,
            credit: q.credit != null ? q.credit : Math.round(480 / cfg.questions.length)
          })));
        }
      } catch (_) {
        // keep defaults
      }
    }
    loadQuiz();
    return () => { active = false; };
  }, []);

  React.useEffect(() => {
    let active = true;
    async function checkLock() {
      if (!user?.uid) return;
      try {
        const lock = await getAssessmentLock(user.uid);
        if (!active) return;
        setLocked(!!lock?.locked);
      } catch (e) {
        // default unlocked if lock doc missing
        setLocked(false);
      } finally {
        if (active) setChecking(false);
      }
    }
    checkLock();
    return () => { active = false; };
  }, [user]);

  const percent = useMemo(() => {
    const total = questions.length;
    let pts = 0;
    questions.forEach((q) => {
      const correctVal = Number.isInteger(q.correct) ? q.options[q.correct] : q.correct;
      if (answers[q.id] === correctVal) {
        pts += q.credit != null ? q.credit : Math.round(480 / total);
      }
    });
    // Convert credit-based score (out of ~480) to percentage for display
    const pct = Math.round((pts / 480) * 100);
    return Math.min(100, Math.max(0, pct));
  }, [answers, questions]);

  const handleSubmit = async () => {
    setError("");
    if (!user?.uid) return;
    if (Object.keys(answers).length !== questions.length) {
      setError("Please answer all questions");
      return;
    }
    setSubmitting(true);
    try {
      const sc = percent;
      const passed = sc >= 80;
      await saveAssessmentResult(user.uid, sc, passed, { version: 1 });
      setScore(sc);
      // Lock immediately after first submission (pass or fail)
      await lockAssessmentForUser(user.uid, { reason: "submitted", lockedBy: user.uid });

      if (passed) {
        // Award once using duplicate-safe path
        await creditService.awardAutoCredits(user.uid, "assessment_passed", "initial", 480);
        navigate("/unlock");
      } else {
        navigate("/student/learning/paths");
      }
    } catch (e) {
      setError(e.message || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AdminLayout title="Assessment">
      <Box sx={{ p: 3 }}>
        <Card sx={{ maxWidth: 900, mx: "auto", borderTop: "6px solid #C1272D" }}>
          <CardContent>
            <Typography variant="h5" sx={{ fontWeight: 800, color: "#003366", mb: 1 }}>
              Initial Assessment
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              Score at least 80% to earn 480 credits and unlock your dashboard.
            </Typography>

            {checking && (
              <Box sx={{ py: 4, textAlign: "center" }}>
                <LinearProgress />
                <Typography sx={{ mt: 2 }} color="text.secondary">Checking assessment access…</Typography>
              </Box>
            )}

            {!checking && locked && userProfile?.role !== "admin" && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Your assessment is locked after the initial submission. Please contact an administrator to unlock it for re-submission.
              </Alert>
            )}

            {score !== null && (
              <Box sx={{ mb: 2 }}>
                <LinearProgress value={score} variant="determinate" sx={{ height: 10, borderRadius: 2 }} />
                <Typography sx={{ mt: 1 }} color="text.secondary">Score: {score}%</Typography>
              </Box>
            )}

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {!checking && !locked && (
              <Grid container spacing={3}>
              {questions.map((q, idx) => (
                  <Grid key={q.id} item xs={12}>
                    <Box sx={{ p: 2, borderRadius: 2, border: "1px solid #e0e0e0", backgroundColor: "#fafafa" }}>
                      <Typography sx={{ fontWeight: 600, mb: 1, color: "#003366" }}>
                        {idx + 1}. {q.text}
                      </Typography>
                      <RadioGroup
                        value={answers[q.id] || ""}
                        onChange={(e) => setAnswers(prev => ({ ...prev, [q.id]: e.target.value }))}
                      >
                      {q.options.map((opt, i) => (
                          <FormControlLabel
                          key={i}
                          value={opt}
                            control={<Radio />}
                          label={opt}
                          />
                        ))}
                      </RadioGroup>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            )}

            {!checking && !locked && (
              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
                <Button
                  variant="contained"
                  disabled={submitting}
                  onClick={handleSubmit}
                  sx={{
                    px: 4,
                    background: "linear-gradient(135deg,#003366,#004C99)",
                    "&:hover": { background: "linear-gradient(135deg,#00264d,#003d7a)" }
                  }}
                >
                  {submitting ? "Submitting..." : "Submit"}
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>
    </AdminLayout>
  );
}
